/*
 * product-form.js
 * iOS-hardened Add to Cart handler.
 *
 * Changes vs. prior minified build:
 *  1. Removes `this.submitButton.disabled = true` during fetch. On iOS Safari
 *     the native `disabled` attribute paints a grayed button flash that can
 *     mask our custom `.loading` state. We rely on `aria-disabled` + the
 *     short-circuit check in `onSubmitHandler` to prevent double-submit.
 *  2. Enforces a minimum visible loading duration (MIN_LOADING_MS). On fast
 *     networks / prefetched responses, iOS can resolve `fetch()` before the
 *     browser paints the `.loading` class transition, so the spinner never
 *     appears to the user. We defer the cleanup in `finally` until at least
 *     MIN_LOADING_MS has elapsed since click.
 *  3. Uses rAF to flush the loading-class paint before the fetch microtask
 *     chain kicks in, so iOS reliably paints the spinner frame.
 */
(function () {
  if (customElements.get("product-form")) return;

  const MIN_LOADING_MS = 420;

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector("form");
      if (!this.form) return;

      if (this.variantIdInput) this.variantIdInput.disabled = false;

      this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
      this.cart =
        document.querySelector("cart-notification") ||
        document.querySelector("cart-drawer");
      this.submitButton = this.querySelector('[type="submit"]');
      this.submitButtonText = this.submitButton?.querySelector("span");

      if (document.querySelector("cart-drawer")) {
        this.submitButton?.setAttribute("aria-haspopup", "dialog");
      }

      this.hideErrors = this.dataset.hideErrors === "true";
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      // Re-resolve the submit button at submit time. It is cached in the
      // constructor, but on a product that loaded on a sold-out variant the
      // buttons render as "register/notify" (no [type=submit]) so the cache is
      // null; after a variant switch to an available variant the ATC button is
      // swapped back in (product-info.js) and we must pick it up here, else the
      // recovered Add-to-cart silently bails.
      const liveSubmit = this.querySelector('[type="submit"]');
      if (!liveSubmit) return;
      if (this.submitButton !== liveSubmit) {
        this.submitButton = liveSubmit;
        this.submitButtonText = liveSubmit.querySelector("span");
        if (document.querySelector("cart-drawer")) {
          liveSubmit.setAttribute("aria-haspopup", "dialog");
        }
      }
      if (this.submitButton.getAttribute("aria-disabled") === "true") return;

      if (window._cartPerfDebug) performance.mark("cart:atc:click");

      const clickStart = performance.now();
      this.handleErrorMessage();

      // Enter loading state. No native `disabled` — it can mask custom paint on iOS.
      this.submitButton.setAttribute("aria-disabled", "true");
      this.submitButton.setAttribute("aria-busy", "true");
      this.submitButton.classList.add("loading");
      const spinner = this.querySelector(".loading__spinner");
      if (spinner) spinner.classList.remove("hidden");

      // Force a paint of the loading state before the fetch microtask chain runs.
      // Safari on iOS can coalesce class changes with the fetch resolution if we
      // don't yield here, resulting in no visible spinner at all.
      const doFetch = () => this._performFetch(clickStart);
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => requestAnimationFrame(doFetch));
      } else {
        doFetch();
      }
    }

    _performFetch(clickStart) {
      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append(
          "sections",
          this.cart.getSectionsToRender().map((s) => s.id)
        );
        formData.append("sections_url", window.location.pathname);
      }
      config.body = formData;

      // Guard the ATC request so a stalled mobile connection can't hang the
      // spinner forever. On timeout the fetch rejects (AbortError) and the
      // existing .catch/.finally restore the button so the shopper can retry.
      // Matches the AbortSignal.timeout(1e4) convention already in cart.js.
      if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
        config.signal = AbortSignal.timeout(10000);
      }

      if (window._cartPerfDebug) performance.mark("cart:atc:fetchStart");

      fetch(`${routes.cart_add_url}`, config)
        .then((r) => r.json())
        .then((response) => {
          if (window._cartPerfDebug)
            performance.mark("cart:atc:responseReceived");

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: "product-form",
              productVariantId: formData.get("id"),
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);
            const soldOutMsg = this.submitButton.querySelector(
              ".sold-out-message"
            );
            if (soldOutMsg) {
              this.submitButton.setAttribute("aria-disabled", "true");
              this.submitButtonText?.classList.add("hidden");
              soldOutMsg.classList.remove("hidden");
            }
            this.error = true;
            return;
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          this.error = false;
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: formData.get("id"),
            cartData: response,
          });

          const quickAddModal = this.closest("quick-add-modal");
          if (quickAddModal) {
            document.body.addEventListener(
              "modalClosed",
              () => {
                setTimeout(() => {
                  this.cart.renderContents(response);
                });
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }

          if (window._cartPerfDebug) {
            performance.mark("cart:atc:domUpdated");
            performance.measure(
              "ATC: click → fetch start",
              "cart:atc:click",
              "cart:atc:fetchStart"
            );
            performance.measure(
              "ATC: network time",
              "cart:atc:fetchStart",
              "cart:atc:responseReceived"
            );
            performance.measure(
              "ATC: response → DOM",
              "cart:atc:responseReceived",
              "cart:atc:domUpdated"
            );
            console.table(
              performance
                .getEntriesByType("measure")
                .filter((m) => m.name.startsWith("ATC:"))
                .map((m) => ({ name: m.name, ms: Math.round(m.duration) }))
            );
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          const elapsed = performance.now() - clickStart;
          const wait = Math.max(0, MIN_LOADING_MS - elapsed);
          setTimeout(() => this._exitLoading(), wait);
        });
    }

    _exitLoading() {
      if (!this.submitButton) return;
      this.submitButton.classList.remove("loading");
      this.submitButton.removeAttribute("aria-disabled");
      this.submitButton.removeAttribute("aria-busy");
      if (this.cart && this.cart.classList.contains("is-empty")) {
        this.cart.classList.remove("is-empty");
      }
      const spinner = this.querySelector(".loading__spinner");
      if (spinner) spinner.classList.add("hidden");
    }

    handleErrorMessage(msg = false) {
      if (this.hideErrors) return;
      this.errorMessageWrapper =
        this.errorMessageWrapper ||
        this.querySelector(".product-form__error-message-wrapper");
      if (!this.errorMessageWrapper) return;
      this.errorMessage =
        this.errorMessage ||
        this.errorMessageWrapper.querySelector(".product-form__error-message");
      this.errorMessageWrapper.toggleAttribute("hidden", !msg);
      if (msg && this.errorMessage) this.errorMessage.textContent = msg;
    }

    toggleSubmitButton(disable = true, text) {
      if (!this.submitButton) return;
      if (disable) {
        this.submitButton.setAttribute("disabled", "disabled");
        if (text && this.submitButtonText) this.submitButtonText.textContent = text;
      } else {
        this.submitButton.removeAttribute("disabled");
        if (this.submitButtonText)
          this.submitButtonText.textContent = window.variantStrings.addToCart;
      }
    }

    get variantIdInput() {
      return this.form?.querySelector("[name=id]");
    }
  }

  customElements.define("product-form", ProductForm);
})();
